import api from './api-service';

/**
 * Passkeys, browser side.
 *
 * Most of this file is translation. WebAuthn deals in `ArrayBuffer`s, JSON does not, and the two
 * have to meet somewhere: the API sends base64url strings, the browser wants buffers, and the
 * credential it hands back is buffers again. `PublicKeyCredential.toJSON()` would do the return
 * leg, but only on browsers new enough to have it, and a sign-in that silently fails on an
 * older one is worse than a few lines of encoding.
 */

const options = { validateStatus: () => true };

/**
 * Reads its own status rather than letting a refusal reach the shared response interceptor,
 * which treats any 401 as an expired session: it clears the cookie and reloads. Mid-registration
 * that would throw away a ceremony the person is part-way through.
 */
/**
 * What the API answers when an account with nothing to confirm with -- no password, no passkey --
 * asks to register one without a fresh sign-in behind it. Mirrors `Passkeys::REAUTHENTICATION_REQUIRED`
 * on the API; the enrollment screen turns it into a "sign in again" path rather than a dead end.
 */
export const REAUTHENTICATION_REQUIRED = 'Sign in again before adding a passkey';

const read = (request, unavailable) => {
	return request.then(({ data, status } = {}) => {
		if(status >= 200 && status < 300) {
			return data;
		}

		throw new Error(status >= 500 ? unavailable : (data?.error || unavailable));
	});
};

const decode = (value) => {
	const padded = value
		.replace(/-/g, '+')
		.replace(/_/g, '/');
	const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
	const bytes = new Uint8Array(binary.length);

	for(let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}

	return bytes;
};

const encode = (buffer) => {
	const bytes = new Uint8Array(buffer);
	let binary = '';

	for(let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]);
	}

	return btoa(binary)
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');
};

/**
 * The options as the API sent them, with the fields WebAuthn requires as buffers turned into
 * buffers. Everything else passes through untouched, so a field the API starts sending does not
 * need a change here.
 */
const toPublicKey = (publicKey) => {
	const prepared = { ...publicKey, challenge: decode(publicKey.challenge) };

	if(publicKey.user) {
		prepared.user = { ...publicKey.user, id: decode(publicKey.user.id) };
	}

	['excludeCredentials', 'allowCredentials'].forEach((key) => {
		if(Array.isArray(publicKey[key])) {
			prepared[key] = publicKey[key].map((credential) => ({
				...credential,
				id: decode(credential.id)
			}));
		}
	});

	return prepared;
};

/**
 * The credential, as JSON the API can parse.
 */
const fromCredential = (credential) => {
	const response = credential.response;

	const payload = {
		id: credential.id,
		rawId: encode(credential.rawId),
		type: credential.type,
		clientExtensionResults: credential.getClientExtensionResults
			? credential.getClientExtensionResults()
			: {},
		response: {
			clientDataJSON: encode(response.clientDataJSON)
		}
	};

	// A registration carries the attestation; a sign-in carries the signature. Which one is
	// present is how the API tells the two ceremonies apart, so neither is faked in.
	if(response.attestationObject) {
		payload.response.attestationObject = encode(response.attestationObject);

		if(response.getTransports) {
			payload.response.transports = response.getTransports();
		}
	}
	else {
		payload.response.authenticatorData = encode(response.authenticatorData);
		payload.response.signature = encode(response.signature);
		payload.response.userHandle = response.userHandle ? encode(response.userHandle) : null;
	}

	return payload;
};

let configuredPromise = null;

const PasskeyService = {
	/**
	 * Whether this deployment has a relying party configured.
	 *
	 * Arrives with the boot `system-check` request, and is read the same way the Google and
	 * Microsoft client ids are — awaiting `configReady` rather than reading a field that may not
	 * be filled in yet. Cached, because every render of the button would otherwise wait again.
	 */
	isConfigured: () => {
		if(!configuredPromise) {
			const ready = window.pixwel?.configReady ?? Promise.resolve();

			configuredPromise = ready
				.catch(() => null)
				.then(() => !!window.pixwel?.passkeys);
		}

		return configuredPromise;
	},

	/**
	 * Whether this browser can do WebAuthn at all.
	 *
	 * Checked before the button is offered: an older browser, or a page served over plain http
	 * from something other than localhost, has no `credentials` to call.
	 */
	isSupported: () => {
		return !!(window.PublicKeyCredential
			&& navigator.credentials
			&& typeof navigator.credentials.create === 'function');
	},

	/**
	 * The caller's passkeys, and whether the deployment can do them.
	 */
	list: () => {
		return read(
			api.client.get('/passkeys', options),
			'Passkey settings are unavailable right now'
		);
	},

	/**
	 * Prove the account with a passkey it already holds.
	 *
	 * What `register` needs before it will create another one, for an account that has one:
	 * the password is the weaker credential and may not exist at all. Leaves a short-lived
	 * grant on the API side, which is the same thing an emailed sign-in link leaves.
	 */
	confirm: async() => {
		const started = await read(
			api.client.post('/passkeys/confirm-options', {}, options),
			'Passkeys are not available right now'
		);

		const credential = await navigator.credentials.get({
			publicKey: toPublicKey(started.options)
		});

		if(!credential) {
			throw new Error('No passkey was used');
		}

		return read(
			api.client.post('/passkeys/confirm', {
				ceremonyId: started.ceremonyId,
				credential: fromCredential(credential)
			}, options),
			'That passkey could not be confirmed'
		);
	},

	/**
	 * Register a passkey: ask for options, let the authenticator make one, store what it made.
	 *
	 * One call rather than three, because the three are useless apart — the ceremony id from
	 * the first is what makes the third meaningful, and it is spent either way.
	 *
	 * `confirm` runs first when the account already holds a passkey: two system prompts in a
	 * row, the first proving who this is and the second making the new credential.
	 */
	register: async({ password, name, confirmWith, on } = {}) => {
		if(confirmWith === 'passkey') {
			await PasskeyService.confirm();
		}

		const started = await read(
			api.client.post('/passkeys/challenge', {
				...(password ? { password } : {}),
				// `phone` asks the API for a cross-platform ceremony, which is what makes the
				// browser show its QR code instead of reaching for the fingerprint reader.
				...(on ? { on } : {})
			}, options),
			'Passkeys are not available right now'
		);

		const credential = await navigator.credentials.create({
			publicKey: toPublicKey(started.options)
		}).catch((e) => {
			// The authenticator was asked to make a second passkey for an account it already
			// holds one for -- `excludeCredentials` doing its job. The raw message names the
			// "relying party" and helps nobody; what the person needs is the way round it.
			if(e?.name === 'InvalidStateError') {
				throw new Error(on === 'phone'
					? 'That device already has this passkey — try a security key, or a device '
						+ 'that is not signed into the same account.'
					: 'This device already has a passkey for Pixwel. Use "Add on a phone" for a '
						+ 'security key or another device.');
			}

			throw e;
		});

		if(!credential) {
			throw new Error('No passkey was created');
		}

		return read(
			api.client.post('/passkeys/register', {
				ceremonyId: started.ceremonyId,
				credential: fromCredential(credential),
				name
			}, options),
			'Could not save that passkey right now'
		);
	},

	rename: ({ id, name }) => {
		return read(
			api.client.post('/passkeys/rename', { id, name }, options),
			'Could not rename that passkey right now'
		);
	},

	remove: ({ id }) => {
		return read(
			api.client.post('/passkeys/delete', { id }, options),
			'Could not remove that passkey right now'
		);
	},

	/**
	 * Sign in with a passkey.
	 *
	 * Nothing is passed in: the authenticator offers whatever it holds for this origin, and the
	 * credential it answers with is what identifies the account. Returns the signed response for
	 * the caller to exchange for a session.
	 */
	/**
	 * Runs a sign-in ceremony, for one address when there is one.
	 *
	 * The address matters: a discoverable credential names its own account, so a ceremony
	 * started without one offers every passkey on the device and signs the person in as
	 * whichever they pick — which is not what a screen showing one email address means. Sending
	 * it lets the API narrow the browser's picker, and bind the ceremony so a passkey for
	 * another account is refused rather than honoured.
	 */
	authenticate: async({ identifier } = {}) => {
		const started = await read(
			api.client.post('/session/passkey-options', identifier ? { identifier } : {}, options),
			'Passkey sign-in is not available right now'
		);

		const credential = await navigator.credentials.get({
			publicKey: toPublicKey(started.options)
		});

		if(!credential) {
			throw new Error('No passkey was used');
		}

		return {
			ceremonyId: started.ceremonyId,
			credential: fromCredential(credential)
		};
	}
};

export default PasskeyService;
export { decode, encode, fromCredential, toPublicKey };
